/**
 * Chat's half of the live stream (unit 26B), heard once, in the shell.
 *
 * - `message_part` shows text as the agent writes it; `message` folds the
 *   stored reply into it (v1.98) and opens what the agent opened. A person's
 *   `message` from another screen joins the thread; this screen's own was
 *   shown as it was typed, and its `screen_id` says so.
 * - `agent_error`, `run_finished`, `run_stopped` settle the run. A finished
 *   run asks the server first: a message sent in the same instant may already
 *   have started the next one.
 * - On open, `GET /api/chat/state` shows a run in flight; on each `connected`,
 *   `GET /api/messages/latest` adds a reply that came while the stream was down,
 *   and the state's `failed` says why a run that failed meanwhile left none.
 * - A write the agent asks for is a line of the thread (2026-10-12), and
 *   `confirmation_resolved` settles it on every screen. On open, only a
 *   request a run is held on joins the thread: one left open on another day
 *   is with the earlier messages.
 * - A job a person started is a box in the thread (`job_updated`), from
 *   whichever screen started it. On open, only a job that still runs joins.
 */
import { useEffect } from "react";

import { api, type LiveEvents, type Schemas } from "../api/client";
import { useLive } from "../shell/live";
import { useLayout } from "../shell/useLayout";
import { screenId } from "./send";
import { useChat } from "./store";

async function running(): Promise<boolean | null> {
  try {
    const { data } = await api.GET("/api/chat/state");
    return data?.running ?? null;
  } catch {
    return null;
  }
}

/** `tile` as the agent says it — 1 and 2 are columns — to the layout's. */
function toSlot(tile: number | string | null | undefined): 0 | 1 | "expand" | undefined {
  if (tile === 1) return 0;
  if (tile === 2) return 1;
  if (tile === "expand") return "expand";
  return undefined;
}

export function openActions(event: LiveEvents["message"]): void {
  const actions =
    event.widget_actions ??
    (event.widget_open
      ? [
          {
            widget: event.widget_open,
            params: event.widget_params ?? null,
            tile: event.widget_open_tile,
          },
        ]
      : []);
  for (const action of actions) {
    useLayout
      .getState()
      .open({ name: action.widget, params: action.params ?? null, tile: toSlot(action.tile) });
  }
}

export function hearMessage(event: LiveEvents["message"]): void {
  const chat = useChat.getState();
  if (event.role === "user") {
    if (event.screen_id === screenId) return;
    chat.mark(chat.addUser(event.content), "sent");
    return;
  }
  chat.reply(event.content, event.message_id ?? null);
  useChat.getState().settle();
  openActions(event);
}

export async function catchUp(): Promise<void> {
  const chat = useChat.getState();
  if (!chat.lines.some((line) => line.role === "user")) return;
  try {
    const { data } = await api.GET("/api/messages/latest");
    if (data) chat.reply(data.content, data.id);
  } catch {
    return;
  }
  let state: Schemas["ChatState"] | undefined;
  try {
    state = (await api.GET("/api/chat/state")).data;
  } catch {
    return;
  }
  if (!state || state.running) return;
  const now = useChat.getState();
  now.settle();
  // A run that failed while the stream was down: its `agent_error` was never
  // heard, so its words come from the state — once, under the question (#61).
  if (state.failed && now.lines.at(-1)?.role === "user") now.say(state.failed.message, true);
}

async function waitedOn(): Promise<void> {
  try {
    const { data } = await api.GET("/api/agent/confirmations/pending");
    for (const item of data?.items ?? []) if (item.waiting) useChat.getState().request(item);
  } catch {
    return;
  }
}

async function jobsRunning(): Promise<void> {
  try {
    const { data } = await api.GET("/api/chat/jobs");
    for (const job of data?.items ?? []) if (job.state === "running") useChat.getState().job(job);
  } catch {
    return;
  }
}

export function useChatLive(): void {
  useEffect(() => {
    void running().then((yes) => yes && useChat.getState().start("thinking"));
    void waitedOn();
    void jobsRunning();
  }, []);

  useLive("job_updated", (event) =>
    useChat.getState().job({ ...event, result: event.result ?? null }),
  );

  useLive("confirmation_required", (event) =>
    useChat.getState().request({
      ...event,
      before: event.before ?? null,
      status: "pending",
      error: null,
      opens: (event.opens as Schemas["Opens"] | null | undefined) ?? null,
    }),
  );
  useLive("confirmation_resolved", (event) =>
    useChat.getState().resolved(event.id, event.status, event.error ?? null),
  );

  useLive("message", hearMessage);
  useLive("message_part", (event) => event.content && useChat.getState().part(event.content));
  useLive("agent_step", (event) => event.words && useChat.getState().stepped(event.words));
  useLive("agent_error", (event) => {
    useChat.getState().settle();
    useChat.getState().say(event.message || "Something went wrong on the agent's side.", true);
  });
  useLive("run_finished", () => {
    void running().then((yes) => yes !== true && useChat.getState().settle());
  });
  useLive("run_stopped", () => {
    useChat.getState().settle();
    useChat.getState().say("Stopped.");
  });
  useLive("connected", () => void catchUp());
}
